import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
// Инбокс подтверждений. Ассистент уже создаёт ApprovalRequest (смена стадии,
// обогащение контакта, письмо, запуск сценария), но увидеть их можно было только
// в той переписке, где они возникли: закрыл чат — запрос потерялся.
import { useMemo, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Popconfirm, Space, Spin, Tag, Tooltip, Typography } from 'antd';

import { ApprovalDetailText } from '../lib/ApprovalDetailText.js';
import type { ColumnsType } from 'antd/es/table/interface';
import { CheckOutlined, CloseOutlined, ReloadOutlined } from '@ant-design/icons';

import { formatDateTime } from '../../../shared/format/index.js';
import { EntityLink } from '../../../shared/ui/EntityLink.js';
import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import type { EntityStatusTone } from '../../../components/entity-detail/EntityStatusChip.js';
import { EntityTable } from '../../../shared/ui/EntityTable.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { FilterBar, FilterSelect } from '../../../shared/ui/FilterBar.js';
import { OverdueDate } from '../../../shared/ui/OverdueDate.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { LIST_ICON_BUTTON_STYLE, LIST_SURFACE_STYLE } from '../../../shared/ui/list-styles.js';
import { QuietEmpty, QuietListSkeleton } from '../../../shared/ui/quiet-states.js';
import type {
  ApprovalItem,
  ApprovalStatus,
  ListApprovalsFilters,
} from '../api/approvals.client.js';
import {
  approvalConsistencyWarnings,
  isApprovalExpired,
  summarizeApprovalPresentation,
  summarizeLegacyApproval,
} from '../lib/approvalSummary.js';
import { summarizeBulkApproval } from '../lib/bulkApprovalSummary.js';
import { useApprovalDecisionMutation, useApprovals } from '../hooks/useApprovals.js';
import { approvalTargetKey, useApprovalTargetLabels } from '../hooks/useApprovalTargetLabels.js';

const { Text } = Typography;

type StatusFilter = ApprovalStatus | 'all';

const STATUS_OPTIONS = [
  {
    value: 'pending',
    get label() {
      return i18n.t('crm.features.map.features.approvals.pages.approvalspage.10');
    },
  },
  {
    value: 'approved',
    get label() {
      return i18n.t('crm.features.map.features.approvals.pages.approvalspage.11');
    },
  },
  {
    value: 'rejected',
    get label() {
      return i18n.t('crm.features.map.features.approvals.pages.approvalspage.12');
    },
  },
  {
    value: 'expired',
    get label() {
      return i18n.t('crm.features.map.features.approvals.pages.approvalspage.13');
    },
  },
  {
    value: 'cancelled',
    get label() {
      return i18n.t('crm.features.map.features.approvals.pages.approvalspage.19');
    },
  },
  {
    value: 'all',
    get label() {
      return i18n.t('crm.features.map.features.approvals.pages.approvalspage.14');
    },
  },
];

const STATUS_TAG: Record<ApprovalStatus, { tone: EntityStatusTone; label: string }> = {
  pending: {
    tone: 'warning',
    get label() {
      return i18n.t('crm.features.map.features.approvals.pages.approvalspage.15');
    },
  },
  approved: {
    tone: 'success',
    get label() {
      return i18n.t('crm.features.map.features.approvals.pages.approvalspage.16');
    },
  },
  rejected: {
    tone: 'default',
    get label() {
      return i18n.t('crm.features.map.features.approvals.pages.approvalspage.17');
    },
  },
  expired: {
    tone: 'default',
    get label() {
      return i18n.t('crm.features.map.features.approvals.pages.approvalspage.18');
    },
  },
  cancelled: {
    tone: 'default',
    get label() {
      return i18n.t('crm.features.map.features.approvals.pages.approvalspage.20');
    },
  },
};

export default function ApprovalsPage(): ReactElement {
  useI18nSubscription();
  const [searchParams, setSearchParams] = useSearchParams();
  // Статус-фильтр живёт в URL: ссылку на очередь можно передать.
  const statusParam = searchParams.get('status');
  const status: StatusFilter = (
    STATUS_OPTIONS.map((option) => option.value) as readonly string[]
  ).includes(statusParam ?? '')
    ? (statusParam as StatusFilter)
    : 'pending';
  const setStatus = (value: StatusFilter): void => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value === 'pending') next.delete('status');
        else next.set('status', value);
        return next;
      },
      { replace: true },
    );
  };
  const filters: ListApprovalsFilters = status === 'pending' ? {} : { status };
  const { data, isLoading, isError, error, refetch, isFetching } = useApprovals(filters);
  const decideMut = useApprovalDecisionMutation();

  const approvals = useMemo(() => data ?? [], [data]);
  const summaries = new Map(
    approvals.map((approval) => [
      approval.id,
      approval.presentation
        ? summarizeApprovalPresentation(approval.presentation)
        : summarizeLegacyApproval(approval),
    ]),
  );
  const targetLabels = useApprovalTargetLabels(
    approvals.map((approval) => summaries.get(approval.id)?.target ?? null),
  );
  const pendingId = decideMut.isPending ? (decideMut.variables?.approvalId ?? null) : null;

  const renderSummary = (approval: ApprovalItem): ReactElement => {
    const summary = summaries.get(approval.id) ?? summarizeLegacyApproval(approval);
    const consistencyWarnings = approvalConsistencyWarnings(approval);
    return (
      <div className="flex min-w-0 flex-col gap-1">
        <span className="break-words font-medium text-gray-900">{summary.action}</span>
        {consistencyWarnings.length > 0 ? (
          <div
            className="flex flex-wrap gap-x-3 gap-y-1"
            data-testid="approval-consistency-warnings"
          >
            {consistencyWarnings.map((warning) => (
              <Text
                key={`${warning.field}:${warning.kind}`}
                type="warning"
                style={{ fontSize: 12 }}
              >
                {warning.kind === 'missing'
                  ? i18n.t('crm.approvals.consistency.missing', {
                      field: warning.field,
                      expected: warning.expected,
                    })
                  : i18n.t('crm.approvals.consistency.mismatch', {
                      field: warning.field,
                      expected: warning.expected,
                      actual: warning.actual ?? '',
                    })}
              </Text>
            ))}
          </div>
        ) : null}
        {summary.details.length > 0 ? (
          <div className="flex min-w-0 flex-col gap-1">
            {summary.details.map((detail) => (
              <ApprovalDetailText key={detail.label} detail={detail} />
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  const renderTarget = (approval: ApprovalItem): ReactElement => {
    const summary = summaries.get(approval.id) ?? summarizeLegacyApproval(approval);
    const { target, presentationTarget } = summary;
    if (presentationTarget) {
      return <Tag>{presentationTarget.label}</Tag>;
    }
    if (target === null)
      return <Text type="secondary">{i18n.t('crm.approvals.detail.name_unavailable')}</Text>;
    if (target.label) {
      return (
        <EntityLink kind={target.kind} id={target.id} label={target.label} asTag withKindPrefix />
      );
    }
    const resolution = targetLabels.get(approvalTargetKey(target));
    if (resolution?.status === 'resolved') {
      return (
        <EntityLink
          kind={target.kind}
          id={target.id}
          label={resolution.label}
          asTag
          withKindPrefix
        />
      );
    }
    if (resolution?.status === 'pending') {
      return (
        <Spin
          size="small"
          aria-label={i18n.t('crm.features.features.approvals.pages.approvalspage.объект')}
        />
      );
    }
    // Never expose an internal identifier when lookup failed or the kind is unsupported.
    return <Text type="secondary">{i18n.t('crm.approvals.detail.name_unavailable')}</Text>;
  };

  const renderStatus = (approval: ApprovalItem): ReactElement => {
    const effective: ApprovalStatus = isApprovalExpired(approval) ? 'expired' : approval.status;
    const tag = STATUS_TAG[effective];
    return <EntityStatusChip tone={tag.tone}>{tag.label}</EntityStatusChip>;
  };

  const renderExecutionResult = (approval: ApprovalItem): ReactElement => {
    if (approval.status !== 'approved') {
      return <Text type="secondary">—</Text>;
    }

    const errorCode = approval.executionOutcome?.errorCode;
    if (errorCode === 'stale_deal_stage' || errorCode === 'stale_write') {
      return (
        <EntityStatusChip tone="warning">
          {i18n.t(
            errorCode === 'stale_write'
              ? 'crm.approvals.execution_stale_write'
              : 'crm.approvals.execution_stale',
          )}
        </EntityStatusChip>
      );
    }
    if (errorCode === 'lease_expired' || errorCode === 'scope_recheck_failed') {
      return (
        <EntityStatusChip tone="warning">
          {i18n.t('crm.approvals.bulk.status.retry')}
        </EntityStatusChip>
      );
    }

    const bulk = summarizeBulkApproval(approval);
    if (bulk) {
      if (!bulk.result) {
        return <Tag>{i18n.t('crm.approvals.execution_pending_verification')}</Tag>;
      }
      const { succeeded, failed, stale, skipped, unfinished } = bulk.result;
      if (unfinished > 0) {
        return (
          <EntityStatusChip tone="processing">
            {i18n.t('crm.approvals.execution_executing')}
          </EntityStatusChip>
        );
      }
      if (succeeded > 0 && failed + stale + skipped > 0) {
        return (
          <EntityStatusChip tone="warning">
            {i18n.t('crm.approvals.bulk.status.partial')}
          </EntityStatusChip>
        );
      }
      if (succeeded > 0) {
        return (
          <EntityStatusChip tone="success">
            {i18n.t('crm.approvals.execution_succeeded')}
          </EntityStatusChip>
        );
      }
      if (stale > 0 && failed === 0 && skipped === 0) {
        return (
          <EntityStatusChip tone="warning">
            {i18n.t('crm.approvals.execution_stale_write')}
          </EntityStatusChip>
        );
      }
      return (
        <EntityStatusChip tone="error">{i18n.t('crm.approvals.execution_failed')}</EntityStatusChip>
      );
    }

    switch (approval.executionOutcome?.status) {
      case 'succeeded':
        return (
          <EntityStatusChip tone="success">
            {i18n.t('crm.approvals.execution_succeeded')}
          </EntityStatusChip>
        );
      case 'failed':
        return (
          <EntityStatusChip tone="error">
            {i18n.t('crm.approvals.execution_failed')}
          </EntityStatusChip>
        );
      case 'executing':
        return (
          <EntityStatusChip tone="processing">
            {i18n.t('crm.approvals.execution_executing')}
          </EntityStatusChip>
        );
      default:
        return <Tag>{i18n.t('crm.approvals.execution_pending_verification')}</Tag>;
    }
  };

  const renderActions = (approval: ApprovalItem): ReactElement => {
    if (approval.status !== 'pending' || isApprovalExpired(approval)) {
      return <Text type="secondary">—</Text>;
    }
    const busy = pendingId === approval.id;
    return (
      <Space size={4}>
        <Tooltip title={i18n.t('crm.features.features.approvals.pages.approvalspage.подтвердить')}>
          <Popconfirm
            title={i18n.t('crm.features.features.approvals.pages.approvalspage.выполнить_действие')}
            description={(summaries.get(approval.id) ?? summarizeLegacyApproval(approval)).action}
            okText={i18n.t('crm.features.features.approvals.pages.approvalspage.выполнить')}
            cancelText={i18n.t('crm.features.features.approvals.pages.approvalspage.отмена')}
            onConfirm={() =>
              // Returning the promise keeps the confirm OK button loading until
              // the decision settles — no second click can fire a duplicate.
              decideMut
                .mutateAsync({ approvalId: approval.id, decision: 'approve' })
                .catch(() => undefined)
            }
          >
            <Button
              size="small"
              type="text"
              loading={busy}
              aria-label={i18n.t(
                'crm.features.features.approvals.pages.approvalspage.подтвердить_2',
              )}
              icon={<CheckOutlined style={{ color: 'var(--ant-color-success)' }} />}
            />
          </Popconfirm>
        </Tooltip>
        <Tooltip title={i18n.t('crm.features.features.approvals.pages.approvalspage.отклонить')}>
          <Popconfirm
            title={i18n.t('crm.features.features.approvals.pages.approvalspage.отклонить_запрос')}
            okText={i18n.t('crm.features.features.approvals.pages.approvalspage.отклонить_2')}
            cancelText={i18n.t('crm.features.features.approvals.pages.approvalspage.отмена_2')}
            okButtonProps={{ danger: true }}
            onConfirm={() =>
              decideMut
                .mutateAsync({ approvalId: approval.id, decision: 'reject' })
                .catch(() => undefined)
            }
          >
            <Button
              size="small"
              type="text"
              danger
              disabled={busy}
              aria-label={i18n.t('crm.features.features.approvals.pages.approvalspage.отклонить_3')}
              icon={<CloseOutlined />}
            />
          </Popconfirm>
        </Tooltip>
      </Space>
    );
  };

  const columns: ColumnsType<ApprovalItem> = [
    {
      title: i18n.t('crm.features.features.approvals.pages.approvalspage.действие'),
      key: 'action',
      render: (_: unknown, approval) => renderSummary(approval),
    },
    {
      title: i18n.t('crm.features.features.approvals.pages.approvalspage.объект'),
      key: 'target',
      width: 200,
      render: (_: unknown, approval) => renderTarget(approval),
    },
    {
      title: i18n.t('crm.features.features.approvals.pages.approvalspage.создано'),
      key: 'createdAt',
      width: 150,
      sorter: (a, b) => new Date(a.createdAt ?? 0).getTime() - new Date(b.createdAt ?? 0).getTime(),
      render: (_: unknown, approval) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {formatDateTime(approval.createdAt)}
        </Text>
      ),
    },
    {
      title: i18n.t('crm.features.features.approvals.pages.approvalspage.истекает'),
      key: 'expiresAt',
      width: 160,
      render: (_: unknown, approval) => (
        <OverdueDate
          iso={approval.expiresAt}
          closed={approval.status !== 'pending'}
          title={i18n.t(
            'crm.features.features.approvals.pages.approvalspage.срок_подтверждения_истек',
          )}
        />
      ),
    },
    {
      title: i18n.t('crm.features.features.approvals.pages.approvalspage.статус'),
      key: 'status',
      width: 130,
      render: (_: unknown, approval) => renderStatus(approval),
    },
    {
      title: i18n.t('crm.approvals.execution_result'),
      key: 'executionResult',
      width: 180,
      render: (_: unknown, approval) => renderExecutionResult(approval),
    },
    {
      title: i18n.t('crm.features.features.approvals.pages.approvalspage.действия'),
      key: 'actions',
      width: 110,
      render: (_: unknown, approval) => renderActions(approval),
    },
  ];

  return (
    <div className="animate-fade-in flex flex-col">
      <PageHeader
        title={i18n.t('crm.features.features.approvals.pages.approvalspage.подтверждения')}
        subtitle={
          <>
            {i18n.t(
              'crm.features.features.approvals.pages.approvalspage.действия_ассистента_которые_не_выполняются',
            )}{' '}
            <span data-testid="approvals-visible-count">
              {approvals.length} {i18n.tc('crm.counts.approvals', approvals.length)}
            </span>
          </>
        }
        actions={
          <Tooltip title={i18n.t('crm.features.features.approvals.pages.approvalspage.обновить')}>
            <Button
              aria-label={i18n.t('crm.features.features.approvals.pages.approvalspage.обновить_2')}
              icon={<ReloadOutlined />}
              loading={isFetching}
              onClick={() => void refetch()}
              style={LIST_ICON_BUTTON_STYLE}
            />
          </Tooltip>
        }
        testId="approvals-page-header"
      />

      <div className="flex flex-col gap-3">
        <FilterBar>
          <FilterSelect
            aria-label={i18n.t(
              'crm.features.features.approvals.pages.approvalspage.фильтр_по_статусу',
            )}
            value={status}
            onChange={(value) => setStatus(value as StatusFilter)}
            options={STATUS_OPTIONS}
          />
        </FilterBar>

        {decideMut.error ? (
          <Alert
            type="error"
            showIcon
            closable
            title={i18n.t(
              'crm.features.features.approvals.pages.approvalspage.не_удалось_применить_решение',
            )}
            description={decideMut.error.message}
          />
        ) : null}

        {decideMut.data?.executionOutcome?.status === 'failed' ? (
          <Alert
            type="error"
            showIcon
            title={i18n.t(
              'crm.features.features.approvals.pages.approvalspage.действие_не_выполнено',
            )}
            description={i18n.t(
              'crm.features.features.approvals.pages.approvalspage.подтверждение_сохранено_но_исполнение_завершилось_ошибкой',
            )}
          />
        ) : null}

        {isError ? (
          <ErrorState
            title={i18n.t(
              'crm.features.features.approvals.pages.approvalspage.не_удалось_загрузить_подтверждения',
            )}
            error={error}
            onRetry={() => void refetch()}
          />
        ) : isLoading ? (
          <div style={LIST_SURFACE_STYLE}>
            <QuietListSkeleton rows={6} />
          </div>
        ) : approvals.length === 0 ? (
          <div style={LIST_SURFACE_STYLE}>
            <QuietEmpty
              text={
                status === 'pending'
                  ? i18n.t(
                      'crm.features.features.approvals.pages.approvalspage.нет_запросов_ожидающих_решения',
                    )
                  : i18n.t('crm.features.features.approvals.pages.approvalspage.записей_нет')
              }
            />
          </div>
        ) : (
          <>
            <div
              className="hidden md:block"
              data-testid="approvals-desktop-table"
              style={LIST_SURFACE_STYLE}
            >
              <EntityTable<ApprovalItem> dataSource={approvals} columns={columns} />
            </div>
            <ul
              className="m-0 flex list-none flex-col gap-3 p-0 md:hidden"
              data-testid="approvals-mobile-list"
            >
              {approvals.map((approval) => (
                <li
                  key={approval.id}
                  className="crm-radius-s min-w-0 max-w-full border border-solid border-[var(--ant-color-border-secondary)] bg-[var(--ant-color-bg-container)] p-3"
                  data-testid={`approval-mobile-item-${approval.id}`}
                >
                  <div className="mb-3 flex min-w-0 flex-wrap items-start justify-between gap-2">
                    {renderStatus(approval)}
                    {renderActions(approval)}
                  </div>
                  {renderSummary(approval)}
                  <dl
                    className="mb-0 mt-3 grid min-w-0 grid-cols-1 gap-x-3 gap-y-1 text-sm sm:grid-cols-[auto_minmax(0,1fr)] sm:gap-y-2"
                    data-testid={`approval-mobile-details-${approval.id}`}
                  >
                    <dt className="text-[var(--ant-color-text-tertiary)]">
                      {i18n.t('crm.approvals.execution_result')}
                    </dt>
                    <dd className="m-0 min-w-0 max-w-full break-words">
                      {renderExecutionResult(approval)}
                    </dd>
                    <dt className="text-[var(--ant-color-text-tertiary)]">
                      {i18n.t('crm.features.features.approvals.pages.approvalspage.объект')}
                    </dt>
                    <dd className="m-0 min-w-0 max-w-full break-all [&_.ant-tag]:max-w-full [&_.ant-tag]:whitespace-normal">
                      {renderTarget(approval)}
                    </dd>
                    <dt className="text-[var(--ant-color-text-tertiary)]">
                      {i18n.t('crm.features.features.approvals.pages.approvalspage.создано')}
                    </dt>
                    <dd className="m-0 min-w-0 max-w-full break-words">
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        {formatDateTime(approval.createdAt)}
                      </Text>
                    </dd>
                    <dt className="text-[var(--ant-color-text-tertiary)]">
                      {i18n.t('crm.features.features.approvals.pages.approvalspage.истекает')}
                    </dt>
                    <dd className="m-0 min-w-0 max-w-full break-words">
                      <OverdueDate
                        iso={approval.expiresAt}
                        closed={approval.status !== 'pending'}
                        title={i18n.t(
                          'crm.features.features.approvals.pages.approvalspage.срок_подтверждения_истек',
                        )}
                      />
                    </dd>
                  </dl>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
